import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import './EmailPreferencesPage.css';

// The preference centre, reached from the footer of any email.
//
// Deliberately usable WITHOUT an account. Most of the addresses on this list belong to people who
// bought one product and never registered, and a preference page that demands a login is a page
// that leaves them with the unsubscribe link as their only option.
//
// The signed token in the link is the only credential. It proves the visitor holds the address and
// nothing else — no name, no order history, nothing that would turn a shared link into a leak.

function EmailPreferencesPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const email = (params.get('e') || '').trim();
  const token = params.get('t') || '';

  const [categories, setCategories] = useState(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!email || !token) {
      setError('This link is incomplete. Please use the one at the bottom of the email.');
      return;
    }
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/email/preferences`, {
        params: { e: email, t: token },
      });
      setCategories(data.categories || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'We could not open your preferences from that link.');
    }
  }, [email, token]);

  useEffect(() => { load(); }, [load]);

  // Saved on the toggle rather than behind a Save button. Someone who came here to turn something
  // off should not be able to leave believing they had.
  const toggle = async (id, subscribed) => {
    setBusy(true);
    setSaved(false);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/api/email/preferences`, {
        e: email, t: token, categories: { [id]: subscribed },
      });
      setCategories(data.categories || []);
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || 'That change could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="email-prefs page-shell">
      <div className="email-prefs-card">
        <h1>Email preferences</h1>
        {email && <p className="email-prefs-address">{email}</p>}

        {error && <p className="email-prefs-error">{error}</p>}

        {!error && !categories && <p className="email-prefs-muted">Loading…</p>}

        {categories && (
          <>
            <div className="email-prefs-list">
              {categories.map((category) => (
                <label key={category.id} className="email-prefs-row">
                  <input
                    type="checkbox"
                    checked={category.subscribed}
                    disabled={busy}
                    onChange={(event) => toggle(category.id, event.target.checked)}
                  />
                  <span>
                    <strong>{category.label}</strong>
                    <span className="email-prefs-muted">{category.description}</span>
                  </span>
                </label>
              ))}
            </div>

            {saved && <p className="email-prefs-saved">Saved.</p>}

            <p className="email-prefs-muted email-prefs-note">
              Emails about your purchases, licences, downloads, and account are not marketing and
              are not affected by these settings — you will keep receiving those.
            </p>

            <button
              type="button"
              className="email-prefs-all-off"
              disabled={busy || categories.every((category) => !category.subscribed)}
              onClick={async () => {
                setBusy(true);
                setSaved(false);
                try {
                  const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/api/email/preferences`, {
                    e: email,
                    t: token,
                    categories: categories.reduce((acc, category) => ({ ...acc, [category.id]: false }), {}),
                  });
                  setCategories(data.categories || []);
                  setSaved(true);
                } catch (err) {
                  setError(err.response?.data?.error || 'That change could not be saved.');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Unsubscribe from everything
            </button>
          </>
        )}

        <p className="email-prefs-muted email-prefs-foot">
          <Link to="/">Back to Vix Algo Trading</Link>
        </p>
      </div>
    </div>
  );
}

export default EmailPreferencesPage;
